import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

class HeartbeatService {
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private readonly HEARTBEAT_INTERVAL = 10000; // 10 seconds
  private currentUserId: string | null = null;
  private currentSessionId: string | null = null;

  constructor() {
    // Set up page visibility handling
    this.setupVisibilityHandling();
  }

  // Start heartbeat for a user in a session
  startHeartbeat(userId: string, sessionId: string): void {
    this.currentUserId = userId;
    this.currentSessionId = sessionId;
    
    // Clear existing heartbeat
    this.stopHeartbeat();
    
    // Send initial heartbeat
    this.sendHeartbeat();
    
    // Set up regular heartbeat
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, this.HEARTBEAT_INTERVAL);
    
  }

  // Stop heartbeat
  stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.currentUserId && this.currentSessionId) {
    }
    
    this.currentUserId = null;
    this.currentSessionId = null;
  }

  // Send heartbeat to Firestore
  private async sendHeartbeat(): Promise<void> {
    if (!this.currentUserId || !this.currentSessionId) {
      return;
    }

    try {
      // Update user's last seen timestamp
      const userRef = doc(db, 'users', this.currentUserId);
      await setDoc(userRef, {
        lastSeenAt: serverTimestamp(),
        isOnline: true
      }, { merge: true });

      // Update session user's last seen timestamp
      const sessionUserRef = doc(db, 'sessions', this.currentSessionId, 'session_users', this.currentUserId);
      await setDoc(sessionUserRef, {
        lastSeenAt: serverTimestamp(),
        isActive: true
      }, { merge: true });

    } catch (error) {
      console.error('Heartbeat error:', error);
    }
  }

  // Set up page visibility handling
  private setupVisibilityHandling(): void {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, stop heartbeat
        this.stopHeartbeat();
      } else if (this.currentUserId && this.currentSessionId) {
        // Page is visible again, restart heartbeat
        this.startHeartbeat(this.currentUserId, this.currentSessionId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // Mark user as offline (for cleanup)
  async markUserOffline(): Promise<void> {
    if (!this.currentUserId) {
      return;
    }

    try {
      const userRef = doc(db, 'users', this.currentUserId);
      await setDoc(userRef, {
        isOnline: false,
        lastSeenAt: serverTimestamp()
      }, { merge: true });

      if (this.currentSessionId) {
        const sessionUserRef = doc(db, 'sessions', this.currentSessionId, 'session_users', this.currentUserId);
        await setDoc(sessionUserRef, {
          isActive: false,
          lastSeenAt: serverTimestamp()
        }, { merge: true });
      }

    } catch (error) {
      console.error('Failed to mark user as offline:', error);
    }
  }
}

export const heartbeatService = new HeartbeatService();
export default heartbeatService;
